// DispatchDeck - Account Management View
// Plan info, SMS balance, add-ons, account info, cancel subscription

import {
  db,
  auth,
  functions,
  httpsCallable,
  doc,
  getDoc,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  signOut
} from '../firebase-config.js?v=1868';
// Same CDN build firebase-config.js loads, so these act on the SAME auth instance.
import { reauthenticateWithCredential, reauthenticateWithPopup } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-auth.js';
// TASK-9269 — Connect Claude card (MCP tokens, approvals, activity log).
// integ/2098 perf budget — LAZY: loaded when the Account page renders (see the mount below), not on the cold load.
// claudeConnector.js?v=30 notes: v30: TASK-10249 S2 typed job/ticket/shift cards from server card data, decided-elsewhere line, Approving as, cap notice · v29: TASK-10249 requests moved to the Approvals page (#/approvals, views/approvals.js imports the same ?v=) · v28: TASK-10194 follow-up one row per live connection + collapsed History + phone cards + "<name> (Owner)" (onmain) · v27: TASK-10110 Approve selected (low-risk batch, on integ/2131) · v26: TASK-10194 S2 real expiry with the year + Replaced status (integ/2129) · TASK-9566 Disconnect all + login-inactive status (integ/2129) · TASK-10132 key expiry + Create key always answers in the card (integ/2128) · TASK-9494 starter kit (on2125) · TASK-10140 send_memo preview label · TASK-10111 approve-link reasons (no Claude module / list not loaded) · integ/2120 = TASK-9961 + TASK-9803 S1 · TASK-9803 S1 approve link opens its one request · TASK-9961 Team Notes for AI apps · TASK-9970 AI app switches · TASK-9170 gauge mark · TASK-9572 notify_crew preview + second-approver copy · TASK-9848 per-row approve lock · TASK-9820 partner firm in the activity log · TASK-9683 s2 per-person rules link · TASK-9683 second approver · TASK-9542 typed-confirm delete card · TASK-9587 Review in Job Builder

// TASK-9808 — owner approves / turns off a compliance partner's AI access.
import { mountPartnerAiConsent } from './partnerAiConsent.js?v=3'; // TASK-9170 gauge mark

// Text-node escaping for the tenant-controlled strings this view renders into
// innerHTML (company name, owner email, tenant id, phone). escapeHtml is the
// TEXT helper — every sink below is inside an element body, never an attribute,
// so escAttr is not the right tool here. Local fallback so a missing
// sharedFormat.js degrades to escaped output, never to a raw sink.
const esc = (v) => (typeof window !== 'undefined' && window.escapeHtml)
  ? window.escapeHtml(v == null ? '' : String(v))
  : String(v == null ? '' : v).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

// Attribute-context escaper (quotes too). esc() above is the TEXT helper and
// window.escapeHtml is DOM-based (leaves quotes alone), so an attribute value
// gets its own always-quote-safe pass.
const escAttr = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

export function initAccount(container, tenantId) {
  const tenantData = window.AppState?.tenantData || {};
  const isFounder = window.isFounderAccount?.() || false;
  const isVip = window.isVipAccount?.() || false;
  const isDemo = window.AppState?.isDemo || false;
  // Owner-only actions (Stripe portal, Cancel Subscription) are hard-gated
  // server-side — createBillingPortalSession (functions/src/index.js:16266) and
  // cancelSubscription (:23083) both throw permission-denied unless
  // tenant.ownerId === context.auth.uid. This mirrors that gate in the UI so a
  // non-owner member isn't shown buttons that can only ever error. Client-side
  // is cosmetic; the server remains the boundary.
  const isOwner = !!(tenantData.ownerId && window.AppState?.user?.uid
    && tenantData.ownerId === window.AppState.user.uid);

  // TASK-9432 — Upgrade / Manage Billing come from the ONE shared rule in
  // app.js (window.getPlanBillingControls), the same one the Settings billing
  // row uses. Missing helper -> show neither (fail closed; the server is the gate).
  const billing = (typeof window.getPlanBillingControls === 'function')
    ? window.getPlanBillingControls(tenantData, {
        tenantId, uid: window.AppState?.user?.uid, isIOS: !!window.isIOSApp, isDemo,
      })
    : { showUpgrade: false, showManageBilling: false };

  // TASK-4665-S3 — the SMS pack buy/top-up button comes from the ONE shared rule
  // in app.js (window.getSmsPackPurchaseControl), same as the meter CTAs and the
  // SMS upgrade modal. Missing helper -> no button (fail closed).
  const smsBuy = (typeof window.getSmsPackPurchaseControl === 'function')
    ? window.getSmsPackPurchaseControl(tenantData, {
        tenantId, uid: window.AppState?.user?.uid, isIOS: !!window.isIOSApp, isDemo,
      })
    : { show: false, isTopUp: false, label: '' };

  // TASK-9433 — +50-vehicle blocks. Allowance + buttons come from the ONE shared
  // rule in app.js (window.getVehicleAllowance / getVehicleBlockControl), mirroring
  // setVehicleBlocks on the server. Missing helper -> no Vehicles section.
  const vehicles = (typeof window.getVehicleAllowance === 'function' && typeof window.getVehicleBlockControl === 'function')
    ? {
        allowance: window.getVehicleAllowance(tenantData, { tenantId }),
        ctl: window.getVehicleBlockControl(tenantData, {
          tenantId, uid: window.AppState?.user?.uid, isIOS: !!window.isIOSApp, isDemo,
        }),
      }
    : null;

  // Bottom padding: the mobile lock + assistant FABs float bottom-right (lock
  // tops out ~196px up on a phone) and covered the Connect Claude Revoke
  // buttons (TASK-9269 E2E, owner-400px). The padding lets any row scroll clear.
  container.innerHTML = `<div class="account-view" id="account-view-root" style="padding-bottom:calc(220px + env(safe-area-inset-bottom, 0px));"></div>`;
  const root = document.getElementById('account-view-root');

  renderAccountView(root, tenantData, { isFounder, isVip, isDemo, isOwner, tenantId, billing, smsBuy, vehicles });
}

function renderAccountView(root, data, opts) {
  const { isFounder, isVip, isDemo, isOwner, tenantId, billing = {}, smsBuy = { show: false, isTopUp: false, label: '' }, vehicles = null } = opts;
  const twilio = data.twilio || {};

  // Plan info (TASK-9473) — one pure function so the trial / expired / active
  // wording is testable. Trial days come from getTrialStatus, never a guess.
  const trial = (typeof window.getTrialStatus === 'function') ? window.getTrialStatus() : null;
  const { planName, planPrice, planBadge } = buildPlanSummary(data, { isFounder, isVip, isDemo, trial });

  // "What's included" — driven by hasAddOnPackage + hasAiPack, NOT isProAccount
  // alone (legacy addons.* flags on TITS/demo/sandboxes; an expired trial is
  // plan:'base' so isProAccount is true but HR/BI are not).
  const includedItems = buildIncludedItems(data, {
    isFounder, isVip, isDemo, trial,
    has: (k) => (typeof window.hasAddOnPackage === 'function' ? !!window.hasAddOnPackage(k) : false),
    aiPack: () => (typeof window.hasAiPack === 'function' ? !!window.hasAiPack() : false),
  });
  const includedHtml = includedItems.map((it) => `
    <li class="account-included-item ${it.on ? 'included-on' : 'included-off'}">
      <span class="material-icons" aria-hidden="true">${it.on ? 'check_circle' : 'lock'}</span>
      <span>${esc(it.label)}</span>
      <span class="account-included-sr">${it.on ? 'included' : 'not included'}</span>
    </li>`).join('');

  // Account info
  const company = data.companyName || data.company || 'Your Company';
  const email = data.ownerEmail || window.AppState?.user?.email || '';
  const memberSince = data.createdAt?.toDate?.()
    ? data.createdAt.toDate().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : data.subscribedAt
      ? new Date(data.subscribedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      : '—';

  // Phone number display
  const phoneDisplay = twilio.phoneNumber
    ? window.formatPhoneDisplay?.(twilio.phoneNumber) || twilio.phoneNumber
    : twilio.isPromo ? 'Shared number (promo)' : '—';

  // Plan, SMS balance/packs, add-ons and the Danger Zone are the OWNER's: every
  // purchase/portal/cancel callable refuses a non-owner server-side
  // (_baseCheckoutRefusal, _smsPackCheckoutRefusal, createBillingPortalSession,
  // cancelSubscription all check tenant.ownerId === auth.uid). Now that Account
  // has its own nav entry for every member, a non-owner gets Connect Claude +
  // Account Info and a one-line pointer instead of billing they cannot act on.
  // Cosmetic — the server stays the boundary. Demo keeps the full read-only
  // view (demo parity; every buy/portal button is already off for demo).
  const smsBtnLabel = smsBuy.isTopUp
    ? 'Top up SMS \u2014 $25 = 1,500 messages'
    : (twilio.isPromo ? 'Upgrade SMS \u2014 $25 = 1,500 messages' : 'Get SMS credits \u2014 $25 = 1,500 messages');
  // TASK-9816 — SMS auto-recharge. The owner OR a billing-module member may
  // change it (server: setSmsAutoRecharge -> _moneyEditorDecision). The owner
  // card shows it; a non-owner billing editor gets its own small card below.
  // Non-billable list is app.js's (window.SMS_PACK_NON_BILLABLE_TENANTS, the
  // mirror of the server's NON_BILLABLE_TENANTS); the server refuses them anyway.
  const nonBillable = Array.isArray(window.SMS_PACK_NON_BILLABLE_TENANTS) && window.SMS_PACK_NON_BILLABLE_TENANTS.includes(tenantId);
  const canEditAutoRecharge = !isDemo && !nonBillable
    && (isOwner || (typeof window.canEditMoney === 'function' && window.canEditMoney() === true));
  const autoRechargeBlock = `<div id="account-sms-auto-recharge">${buildAutoRechargeHtml(data.smsAutoRecharge, { canEdit: canEditAutoRecharge, isDemo })}</div>`;
  const ownerSections = (isOwner || isDemo) ? `
    <!-- Plan -->
    <div class="account-section">
      <div class="account-section-title">
        <span class="material-icons">credit_card</span> Plan
      </div>
      <div class="account-plan-card">
        <div class="account-plan-info">
          <div class="account-plan-name">${esc(planName)}</div>
          <div class="account-plan-price"${billing.partnerPays ? ' id="account-plan-partner-pays"' : ''}>${esc(billing.partnerPays ? `Paid for by ${billing.partnerName || 'your compliance partner'}` : planPrice)}</div>
          ${billing.partnerSwitch ? `<div class="account-plan-price" id="account-plan-partner-switch">${esc(`${billing.partnerSwitch.partnerName} takes over your subscription on ${new Date(billing.partnerSwitch.atMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}. You pay nothing after that.`)}</div>` : ''}
          ${planBadge}
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;">
          ${billing.showManageBilling ? `
            <button class="btn btn-secondary btn-sm" onclick="window.openBillingPortal?.()">
              <span class="material-icons" style="font-size:16px;">receipt_long</span> Manage Billing
            </button>
          ` : ''}
          ${billing.showUpgrade ? `
            <button class="btn btn-primary btn-sm" onclick="window.startCheckout?.('base')">
              <span class="material-icons" style="font-size:16px;">star</span> Upgrade
            </button>
          ` : ''}
        </div>
      </div>
      <div class="account-included-title">What&rsquo;s included</div>
      <ul class="account-included-list">${includedHtml}</ul>
    </div>

    ${vehicles ? `
    <!-- TASK-9433 — Vehicles: 50 included, +50 for $25/mo per block -->
    <div class="account-section" id="account-vehicles">
      <div class="account-section-title">
        <span class="material-icons">local_shipping</span> Vehicles
      </div>
      <div style="font-size:0.95rem;color:var(--text-primary);">
        ${vehicles.allowance.unlimited
          ? 'No vehicle limit on this account.'
          : `<strong><span id="account-vehicle-count">…</span> of ${vehicles.allowance.limit}</strong> active vehicles`}
      </div>
      ${vehicles.allowance.unlimited ? '' : `
        <div style="font-size:0.85rem;color:var(--text-secondary);margin-top:4px;">
          ${vehicles.allowance.included} included${vehicles.allowance.blocks > 0 ? ` + ${vehicles.allowance.blocks} × 50-vehicle block${vehicles.allowance.blocks === 1 ? '' : 's'} ($${vehicles.allowance.blocks * 25}/mo)` : ''}. Trucks and trailers both count; drivers and users are unlimited.
        </div>
        <div id="account-vehicle-over" style="display:none;font-size:0.85rem;color:var(--text-primary);margin-top:6px;"></div>
        ${(vehicles.ctl.canAdd || vehicles.ctl.canRemove) ? `
          <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:12px;">
            ${vehicles.ctl.canAdd ? `
              <button class="btn btn-primary btn-sm" onclick="window.changeVehicleBlocks?.(1)">
                <span class="material-icons" style="font-size:16px;">add</span> Add 50 vehicles — $25/mo
              </button>` : ''}
            ${vehicles.ctl.canRemove ? `
              <button class="btn btn-secondary btn-sm" onclick="window.changeVehicleBlocks?.(-1)">
                <span class="material-icons" style="font-size:16px;">remove</span> Remove a block
              </button>` : ''}
          </div>` : ''}
        ${vehicles.ctl.note ? `<div style="font-size:0.85rem;color:var(--text-secondary);margin-top:8px;">${esc(vehicles.ctl.note)}</div>` : ''}
      `}
    </div>
    ` : ''}

    <!-- SMS (ONE card: meter, number, one buy button) -->
    <div class="account-section">
      <div class="account-section-title">
        <span class="material-icons">sms</span> SMS
      </div>
      <div id="account-sms-meter" style="margin-bottom:12px;"></div>
      ${buildPendingCreditsHtml(data.smsCreditsPending)}
      ${twilio.phoneNumber ? `
        <div style="font-size:0.85rem;color:var(--text-secondary);margin-top:8px;">
          Dedicated number: <strong style="color:var(--text-primary);">${esc(phoneDisplay)}</strong>
        </div>
      ` : ''}
      ${smsBuy.show ? `
        <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:12px;">
          ${smsBuy.isTopUp ? `
            <select id="account-sms-packs" class="form-select" aria-label="Number of packs" style="min-height:44px;width:auto;">${buildPackOptionsHtml()}</select>
          ` : ''}
          <button class="btn btn-primary" style="min-height:44px;" onclick="${smsBuy.isTopUp ? "window.startCheckout?.('sms_pack', { packs: Number(document.getElementById('account-sms-packs')?.value) || 1 })" : 'window.showSmsUpgradeModal?.()'}">
            <span class="material-icons" style="font-size:18px;">add_circle_outline</span> ${esc(smsBuy.isTopUp ? 'Add credits' : smsBtnLabel)}
          </button>
        </div>
        ${smsBuy.isTopUp ? `<div style="font-size:0.8rem;color:var(--text-muted);margin-top:6px;">One pack is $25 for 1,500 credits, charged to the card on file.</div>` : ''}
      ` : ''}
      ${autoRechargeBlock}
    </div>
  ` : `
    <div class="account-section">
      <div class="account-section-title">
        <span class="material-icons">credit_card</span> Plan &amp; Billing
      </div>
      <div style="font-size:0.9rem;color:var(--text-secondary);">
        Your plan, billing and SMS credits are managed by the account owner.
      </div>
    </div>
    ${canEditAutoRecharge ? `
    <div class="account-section">
      <div class="account-section-title">
        <span class="material-icons">autorenew</span> SMS auto-recharge
      </div>
      ${autoRechargeBlock}
    </div>` : ''}
  `;

  // Owner order: Plan -> SMS -> Account Info -> Connect Claude -> Danger Zone.
  // A non-owner has little else, so Connect Claude stays FIRST for them. It is
  // mounted OUTSIDE the owner gate either way (TASK-9269).
  const showOwnerSections = (isOwner || isDemo);
  const claudeCard = `<div class="account-section" id="account-claude-connector"></div>`;
  // TASK-9768 — customer setup link card: owner or a Roster-module login (the
  // callables enforce the same gate server-side). Not on demo: no owner there.
  const memberModules = window.AppState?.memberAccess?.modules;
  const showSetupCard = !isDemo && (isOwner || (Array.isArray(memberModules) && memberModules.includes('roster')));
  root.innerHTML = `
    ${showOwnerSections ? '' : claudeCard}

    ${ownerSections}

    <!-- Account Info -->
    <div class="account-section">
      <div class="account-section-title">
        <span class="material-icons">info</span> Account Info
      </div>
      <div class="account-info-grid">
        <div class="account-info-label">Company</div>
        <div class="account-info-value">${esc(company)}</div>
        <div class="account-info-label">Owner Email</div>
        <div class="account-info-value">${esc(email)}</div>
        <div class="account-info-label">Member Since</div>
        <div class="account-info-value">${esc(memberSince)}</div>
        <div class="account-info-label">Tenant ID</div>
        <div class="account-info-value" style="font-size:0.75rem;color:var(--text-secondary);font-family:monospace;">
          ${esc(tenantId || '—')}
          ${tenantId ? `<button type="button" class="btn btn-secondary btn-sm account-copy-tenant" data-copy-tenant="${escAttr(tenantId)}" aria-label="Copy tenant ID" style="margin-left:8px;padding:2px 10px;min-height:44px;min-width:44px;"><span class="material-icons" style="font-size:14px;">content_copy</span> Copy</button>` : ''}
        </div>
      </div>
    </div>

    <!-- TASK-10002 — Sign-in & security (Change password). Every signed-in member;
         the card removes itself for demo / anonymous / driver-PIN / shared logins. -->
    ${isDemo ? '' : '<div class="account-section" id="account-sign-in-security"></div>'}

    ${showOwnerSections ? claudeCard : ''}

    ${showSetupCard ? '<div class="account-section" id="account-setup-questionnaire"></div>' : ''}
    ${(isOwner && !isDemo) ? '<div class="account-section" id="account-partner-ai-consent"></div>' : ''}

    <!-- Danger Zone (hidden for founder/VIP, and for non-owner members) -->
    ${(!isFounder && !isVip && !isDemo && isOwner) ? `
      <div class="account-section account-danger-zone">
        <div class="account-section-title">
          <span class="material-icons">warning</span> Danger Zone
        </div>
        <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:var(--spacing-md);">
          Cancelling your subscription will disable access after the current billing period. Your data is retained for 30 days.
        </p>
        <button class="btn btn-danger" onclick="window.showCancelSubscriptionModal?.()" style="width:100%;">
          <span class="material-icons" style="font-size:18px;">cancel</span> Cancel Subscription
        </button>
      </div>
    ` : ''}
  `;

  // integ/2098 perf budget — the card's module loads here (it already renders its data async).
  // TASK-10249: the approval requests (and approve links) live on #/approvals now, not here.
  // A re-render before the chunk arrives must not mount into a detached element.
  // COD r1: mount only into the element THIS render made, once — two renders racing the chunk
  // must not both mount (each mount adds click handlers: one Approve would fire twice).
  const ccHost = document.getElementById('account-claude-connector');
  __ddChunkWrap(import('./claudeConnector.js?v=30')).then((m) => {
    if (!ccHost || !ccHost.isConnected || ccHost.dataset.ccMounted || document.getElementById('account-claude-connector') !== ccHost) return;
    ccHost.dataset.ccMounted = '1';
    m.mountClaudeConnector(document.getElementById('account-claude-connector'), {
      functions, httpsCallable, isDemo,
      // Minting needs a sign-in <= 10 min old; the card prompts with these.
      reauth: { auth, EmailAuthProvider, GoogleAuthProvider, OAuthProvider, reauthenticateWithCredential, reauthenticateWithPopup },
    });
  }).catch(() => {
    const host = ccHost && ccHost.isConnected ? ccHost : null;
    if (host) host.innerHTML = '<p style="font-size:0.85rem;color:var(--text-primary);">Could not load Claude connections. Refresh the page to try again.</p>';
  });

  // TASK-10002 — lazy like the Claude card; mounts only into the element THIS render made.
  const secHost = document.getElementById('account-sign-in-security');
  if (secHost) {
    __ddChunkWrap(import('./changePassword.js?v=1')).then((m) => {
      if (!secHost.isConnected || secHost.dataset.cpMounted) return;
      secHost.dataset.cpMounted = '1';
      m.mountSignInSecurity(secHost, {
        tenantId, functions, httpsCallable,
        api: { auth, EmailAuthProvider, reauthenticateWithCredential, signInWithEmailAndPassword, signOut },
      });
    }).catch(() => {
      if (secHost.isConnected) secHost.innerHTML = '<p style="font-size:0.85rem;color:var(--text-primary);">Could not load sign-in settings. Refresh the page to try again.</p>';
    });
  }

  if (showSetupCard) {
    __ddChunkWrap(import('./setupQuestionnaire.js?v=10027001')).then((m) => m.mountSetupQuestionnaire(document.getElementById('account-setup-questionnaire'), { functions, httpsCallable })).catch(() => {});
  }
  // TASK-9808 — owner only (the server re-checks). Not gated on
  // complianceManagerUid: after an unlink the card must still show a leftover
  // approval so the owner can turn it off (COD r2). The card renders nothing
  // when there is no partner and nothing on file.
  mountPartnerAiConsent(document.getElementById('account-partner-ai-consent'), { functions, httpsCallable, isDemo });

  // Tenant ID copy — the id rides a data- attribute (never an inline onclick
  // string; esc() is a TEXT escaper, not JS-string safe). Read back via dataset.
  root.addEventListener('click', async (ev) => {
    const btn = ev.target.closest?.('[data-copy-tenant]');
    if (!btn || !root.contains(btn)) return;
    const id = btn.dataset.copyTenant || '';
    try {
      await navigator.clipboard.writeText(id);
      window.showToast?.('Tenant ID copied', 'success');
    } catch (e) {
      window.showToast?.('Could not copy \u2014 select the ID and copy it by hand', 'error');
    }
  });

  wireAutoRecharge(root, tenantId, canEditAutoRecharge, isDemo);

  // TASK-9433 — fill in the active-vehicle count (async; leaves "…" if it can't count).
  if ((isOwner || isDemo) && vehicles && !vehicles.allowance.unlimited && typeof window.countActiveVehicles === 'function') {
    window.countActiveVehicles(tenantId).then((n) => {
      const el = document.getElementById('account-vehicle-count');
      if (!el || n == null) return;
      el.textContent = String(n);
      const over = document.getElementById('account-vehicle-over');
      if (over && n > vehicles.allowance.limit) {
        over.style.display = '';
        over.textContent = `You're ${n - vehicles.allowance.limit} over your plan. Add a 50-vehicle block to cover them.`;
      }
    }).catch(() => {});
  }

  // Render the large SMS meter — no duplicate title, no second buy button (the
  // card's own button is the only one).
  if ((isOwner || isDemo) && window.renderSmsCreditMeter) {
    window.renderSmsCreditMeter('account-sms-meter', { variant: 'large', hideTitle: true, hideCta: true });
    const meterEl = document.getElementById('account-sms-meter');
    // The meter renders nothing without a pack (e.g. a VIP on the shared promo
    // number). Say so instead of leaving a hole.
    if (meterEl && !meterEl.innerHTML.trim()) {
      meterEl.textContent = isVip ? 'SMS is included with your account.' : 'No SMS credits yet.';
      meterEl.style.fontSize = '0.9rem';
      meterEl.style.color = 'var(--text-secondary)';
    }
  }
}

// TASK-9816 — SMS pack quantity + auto-recharge. Must match
// functions/src/services/smsPacks.js (CREDITS_PER_PACK, MAX_PACKS, AUTO_RECHARGE).
export const SMS_PACK_CREDITS = 1500;
export const SMS_PACK_MAX = 10;
const AUTO_THRESHOLDS = [100, 200, 300, 500, 750, 1000, 1500];
const AUTO_THRESHOLD_DEFAULT = 300;
const AUTO_CAP_DEFAULT = 4; // Chris 2026-10-15: new default = 4 packs ($100)/month. A saved cap is never changed.
const fmtN = (n) => Number(n).toLocaleString('en-US');

export function buildPackOptionsHtml() {
  let h = '';
  for (let n = 1; n <= SMS_PACK_MAX; n++) {
    h += `<option value="${n}">${n} pack${n === 1 ? '' : 's'} \u2014 $${n * 25} \u00b7 ${fmtN(n * SMS_PACK_CREDITS)} credits</option>`;
  }
  return h;
}

// TASK-9816 — a paid SMS order held for review (webhook _holdSmsPackForReview).
// The owner sees it is coming instead of nothing. Pure.
export function buildPendingCreditsHtml(pending) {
  const rows = pending && typeof pending === 'object' ? Object.values(pending).filter((p) => p && typeof p === 'object') : [];
  if (!rows.length) return '';
  const known = rows.reduce((n, p) => n + (Number.isInteger(p.credits) ? p.credits : 0), 0);
  const msg = known > 0
    ? `${fmtN(known)} credits pending \u2014 we're confirming your order and will add them shortly.`
    : 'An SMS order is pending \u2014 we\u2019re confirming it and will add the credits shortly.';
  return `<div class="account-sms-pending" style="margin:8px 0;padding:8px 12px;border-radius:8px;background:var(--bg-secondary, #F1F5F9);color:var(--text-primary);font-size:0.9rem;">
    <span class="material-icons" style="font-size:16px;vertical-align:-3px;">hourglass_top</span> ${esc(msg)}</div>`;
}

// Pure: the auto-recharge block. `ar` = tenants/{tid}.smsAutoRecharge (may be
// missing). Balances are credits only; the $25 charge is stated in the consent
// line, never on a meter.
export function buildAutoRechargeHtml(ar, { canEdit = false, isDemo = false } = {}) {
  const a = ar || {};
  const on = a.enabled === true;
  const threshold = Number(a.thresholdCredits) || AUTO_THRESHOLD_DEFAULT;
  const cap = Number(a.monthlyCapPacks) || AUTO_CAP_DEFAULT;
  const card = a.card && a.card.last4 ? `${esc(a.card.brand ? String(a.card.brand).toUpperCase() : 'Card')} \u2022\u2022${esc(a.card.last4)}` : '';
  let status;
  if (on) {
    const used = Number(a.chargesThisMonth) || 0;
    status = `<strong style="color:var(--text-primary);">Auto-recharge is ON.</strong> Below ${fmtN(threshold)} credits we add ${fmtN(SMS_PACK_CREDITS)} more, up to ${cap} time${cap === 1 ? '' : 's'} a month${a.monthKey ? ` (${used} used this month)` : ''}.${card ? ` Card: ${card}.` : ''}`;
  } else if (a.disabledReason === 'charge_failed' && a.lastError) {
    status = `<strong style="color:var(--danger, #DC2626);">Auto-recharge turned itself off \u2014 the last charge did not go through.</strong> ${esc(a.lastError.message || '')}`;
  } else {
    status = 'Auto-recharge is off. Turn it on to add a pack automatically when your balance runs low.';
  }
  const thrOpts = AUTO_THRESHOLDS.map((t) => `<option value="${t}"${t === threshold ? ' selected' : ''}>Below ${fmtN(t)} credits</option>`).join('');
  let capOpts = '';
  for (let n = 1; n <= SMS_PACK_MAX; n++) capOpts += `<option value="${n}"${n === cap ? ' selected' : ''}>Up to ${n} pack${n === 1 ? '' : 's'} a month</option>`;
  const controls = canEdit ? `
    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:10px;">
      <select id="ar-threshold" class="form-select" aria-label="Low-balance point" style="min-height:44px;width:auto;">${thrOpts}</select>
      <select id="ar-cap" class="form-select" aria-label="Monthly limit" style="min-height:44px;width:auto;">${capOpts}</select>
    </div>
    <div id="ar-consent" style="font-size:0.8rem;color:var(--text-secondary);margin-top:8px;"></div>
    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:10px;">
      <button type="button" class="btn ${on ? 'btn-secondary' : 'btn-primary'} btn-sm" data-ar-action="save" style="min-height:44px;">${on ? 'Save changes' : 'Turn on auto-recharge'}</button>
      ${on ? '<button type="button" class="btn btn-secondary btn-sm" data-ar-action="off" style="min-height:44px;">Turn off</button>' : ''}
    </div>` : (isDemo ? '<div style="font-size:0.8rem;color:var(--text-secondary);margin-top:6px;">Not available in the demo.</div>' : '');
  return `
    <div class="account-auto-recharge" style="margin-top:16px;padding-top:12px;border-top:1px solid var(--border-color, #E2E8F0);">
      <div style="font-weight:600;color:var(--text-primary);margin-bottom:4px;">Auto-recharge</div>
      <div style="font-size:0.85rem;color:var(--text-secondary);">${status}</div>
      ${controls}
    </div>`;
}

// Pure: the consent line shown next to the button (the server records its own
// copy of the same terms with the choice, setSmsAutoRecharge consentText).
export function autoRechargeConsentLine(threshold, cap) {
  return `When your balance drops below ${fmtN(threshold)} credits, we charge the card on file $25 for one pack of ${fmtN(SMS_PACK_CREDITS)} credits \u2014 at most once an hour and no more than ${cap} time${Number(cap) === 1 ? '' : 's'} a month. The owner gets an email after every charge. If a charge fails, it turns itself off.`;
}

function wireAutoRecharge(root, tenantId, canEdit, isDemo) {
  const host = root.querySelector?.('#account-sms-auto-recharge');
  if (!host || !canEdit) return;
  const refreshConsent = () => {
    const c = host.querySelector('#ar-consent');
    const t = Number(host.querySelector('#ar-threshold')?.value) || AUTO_THRESHOLD_DEFAULT;
    const m = Number(host.querySelector('#ar-cap')?.value) || AUTO_CAP_DEFAULT;
    if (c) c.textContent = autoRechargeConsentLine(t, m);
  };
  refreshConsent();
  host.addEventListener('change', (ev) => { if (ev.target && (ev.target.id === 'ar-threshold' || ev.target.id === 'ar-cap')) refreshConsent(); });
  host.addEventListener('click', async (ev) => {
    const btn = ev.target.closest?.('[data-ar-action]');
    if (!btn || !host.contains(btn) || btn.disabled) return;
    const turnOn = btn.dataset.arAction === 'save';
    const payload = { tenantId, enabled: turnOn };
    if (turnOn) {
      payload.thresholdCredits = Number(host.querySelector('#ar-threshold')?.value) || AUTO_THRESHOLD_DEFAULT;
      payload.monthlyCapPacks = Number(host.querySelector('#ar-cap')?.value) || AUTO_CAP_DEFAULT;
    }
    btn.disabled = true;
    try {
      await httpsCallable(functions, 'setSmsAutoRecharge')(payload);
      window.showToast?.(turnOn ? 'Auto-recharge saved' : 'Auto-recharge turned off', 'success');
      const snap = await getDoc(doc(db, 'tenants', tenantId));
      const fresh = snap.exists() ? (snap.data() || {}) : {};
      if (window.AppState?.tenantData) window.AppState.tenantData.smsAutoRecharge = fresh.smsAutoRecharge;
      host.innerHTML = buildAutoRechargeHtml(fresh.smsAutoRecharge, { canEdit, isDemo });
      refreshConsent();
    } catch (e) {
      window.showToast?.(e?.message || 'Could not save auto-recharge. Please try again.', 'error');
      btn.disabled = false;
    }
  });
}

// TASK-9473 — plan card wording. Pure (no DOM, no window) so it is unit-tested.
// Prices: Self-Serve $250 is the floor. Never invent other numbers here.
export function buildPlanSummary(data, { isFounder, isVip, isDemo, trial } = {}) {
  let planName = 'Free Trial';
  let planPrice = 'then $250/mo';
  let planBadge = '<span class="account-plan-badge badge-trial">Trial</span>';
  if (isFounder) {
    planName = 'Founder Account';
    planPrice = 'Free forever';
    planBadge = '<span class="account-plan-badge badge-founder"><span class="material-icons" style="font-size:14px;">star</span> Founder</span>';
  } else if (isVip) {
    planName = 'VIP Account';
    planPrice = 'Full access';
    planBadge = '<span class="account-plan-badge badge-founder"><span class="material-icons" style="font-size:14px;">diamond</span> VIP</span>';
  } else if (trial && trial.status === 'founder') {
    // getTrialStatus calls a comped / vipAccess tenant 'founder' — never a trial.
    planName = 'Complimentary Account';
    planPrice = 'Full access';
    planBadge = '<span class="account-plan-badge badge-founder"><span class="material-icons" style="font-size:14px;">diamond</span> Comped</span>';
  } else if ((trial && trial.status === 'subscribed') || (!(trial && trial.status === 'expired') && (data.subscription?.plan === 'pro_plus' || data.hasProPlus || data.subscription?.plan === 'pro' || data.hasPro || data.hasBasePlan || data.subscription?.status === 'active'))) {
    // getTrialStatus is the owner of "is this paying" (reads nested AND root
    // subscriptionStatus); the legacy flags only count while it says nothing
    // contradicting them — a stale hasBasePlan on an expired trial is not Active.
    planName = 'DispatchDeck';
    planPrice = '$250/mo';
    planBadge = '<span class="account-plan-badge badge-active">Active</span>';
  } else if (isDemo) {
    planName = 'Demo Account';
    planPrice = 'Free';
    planBadge = '<span class="account-plan-badge badge-trial">Demo</span>';
  } else if (trial && trial.status === 'expired') {
    planName = 'Trial ended';
    planPrice = 'Subscribe to keep going \u2014 $250/mo';
    planBadge = '<span class="account-plan-badge badge-expired">Expired</span>';
  } else if (trial && trial.status === 'trialing' && Number.isFinite(trial.daysRemaining)) {
    const d = trial.daysRemaining;
    planName = `Free trial \u2014 ${d} ${d === 1 ? 'day' : 'days'} left`;
    planPrice = 'then $250/mo';
  }
  return { planName, planPrice, planBadge };
}

// TASK-9473 — "What's included" checklist. `has(key)` = hasAddOnPackage,
// `aiPack()` = hasAiPack. Compliance-only tenants get compliance + hiring only
// (matches setupComplianceOnlySidebar). Demo shows the full set (demo parity).
export function buildIncludedItems(data, { isFounder, isVip, isDemo, trial, has, aiPack } = {}) {
  const complianceOnly = data?.complianceOnly === true;
  const all = !complianceOnly && (isDemo || isFounder || isVip || (!!trial && trial.status === 'founder'));
  const on = (k) => !complianceOnly && (all || !!has?.(k));
  // Smart Tools follows the runtime gate (hasAiPack) — the checklist must never
  // contradict what the app actually lets the tenant do.
  const smart = !complianceOnly && (all || !!aiPack?.());
  return [
    { key: 'dispatch', label: 'Dispatch', on: !complianceOnly },
    { key: 'clockbilling', label: 'Clock & Billing', on: on('clockbilling') },
    { key: 'hr', label: 'HR', on: on('hr') },
    { key: 'bi', label: 'Business Intelligence', on: on('bi') },
    { key: 'crm', label: 'CRM', on: on('crm') },
    { key: 'smarttools', label: 'Smart Tools', on: smart },
    { key: 'compliance', label: 'Compliance & Hiring', on: true },
  ];
}

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
